import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import Header from "../../components/Header";
import RailSixSigmaPanel from "../../components/RailSixSigmaPanel";
import { useAuth } from "../../context/AuthContext";
import {
    Panel, KpiTile, Field, DarkHeader, DateClockChips, statusPill,
    fmtTime, fmtDate, fmtDt, todayLabel, IcnMine, IcnBell, IcnTrain,
    IcnYard, IcnCrane, IcnCheck, FlowStep, FlowArrow, NotificationBell,
    WagonPipelineRibbon, ShortfallBanner,
} from "./shared";

const H_GREEN = "#0B3A1D";      // deep green title bar for the mine
const ACCENT = "#0F5F2A";

export default function MinePlannerDashboard() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [overview, setOverview] = useState(null);
    const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({
        consignment_no: `CGD${Date.now().toString().slice(-9)}`,
        client_name: "", train_id: "", coal_grade: "RB1",
        wagon_count: 50, tonnage: 3250,
        planned_dispatch_at: "", notes: "",
    });
    const [dispatchNotes, setDispatchNotes] = useState("");

    const load = useCallback(async () => {
        try {
            const [cs, ov, nt] = await Promise.all([
                api.get("/rail/consignments?include_completed=true"),
                api.get("/rail/overview"),
                api.get("/rail/notifications?limit=30"),
            ]);
            setRows(cs.data || []);
            setOverview(ov.data);
            setNotifs(nt.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    const create = async () => {
        if (!form.client_name || !form.consignment_no) {
            toast.error("Client name and consignment number are required"); return;
        }
        try {
            await api.post("/rail/consignments", {
                ...form,
                tonnage: Number(form.tonnage) || 0,
                wagon_count: Number(form.wagon_count) || 0,
                planned_dispatch_at: form.planned_dispatch_at
                    ? new Date(form.planned_dispatch_at).toISOString() : null,
            });
            toast.success("Consignment created");
            setForm({ ...form, consignment_no: `CGD${Date.now().toString().slice(-9)}`,
                       client_name: "", train_id: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const dispatchTrain = async (c) => {
        try {
            await api.post(`/rail/consignments/${c.id}/action`,
                           { action: "dispatch", notes: dispatchNotes || undefined });
            toast.success("Train dispatched");
            setDispatchNotes("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const startLoading = async (c) => {
        try {
            await api.post(`/rail/consignments/${c.id}/action`, { action: "start_loading" });
            toast.success("Loading started"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const receiveEmpties = async (c) => {
        try {
            await api.post(`/rail/consignments/${c.id}/action`,
                           { action: "receive_empties_at_mine" });
            toast.success("Cycle complete"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Buckets for the Overview tiles (mine viewpoint)
    const b = overview?.buckets || {};
    const dispatchable = rows.filter((c) => c.status === "loading" || c.status === "draft");
    const active = rows.filter((c) => !["cycle_complete"].includes(c.status));
    const completedToday = rows.filter((c) => c.status === "cycle_complete" &&
        (c.cycle_completed_at || "").startsWith(todayLabel().slice(-4)));
    const totalTonsToday = rows.reduce((s, c) =>
        s + ((c.status === "cycle_complete" || c.status === "completed") &&
             (c.actual_dispatch_at || "").startsWith(new Date().toISOString().slice(0, 10))
              ? Number(c.tonnage || 0) : 0), 0);
    const dispatchedToday = rows.filter((c) => (c.actual_dispatch_at || "")
        .startsWith(new Date().toISOString().slice(0, 10))).length;
    const dispatched = active.filter((c) => c.status === "in_transit_to_transnet");
    const atTransnet = active.filter((c) =>
        ["received_at_transnet", "staged_at_transnet"].includes(c.status));
    const atGrindrod = active.filter((c) =>
        ["received_at_grindrod", "waiting_for_tipping", "tipping_in_progress"].includes(c.status));

    // Auto-select first active train for the Train Progress panel
    const highlight = selected || dispatched[0] || atTransnet[0] || atGrindrod[0] || active[0];

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1600px] mx-auto px-3 md:px-6 py-4 space-y-3">
                <DarkHeader
                    title="Mine Rail Planner Dashboard"
                    subtitle="Customer Mine"
                    tint={H_GREEN}
                    icon={<IcnMine color="#FEF3C7" size={38} />}
                    right={<DateClockChips notifBtn={
                        <NotificationBell notifs={notifs} onChange={load} testid="mine-notif-btn" />
                    } />}
                />

                <ShortfallBanner testid="mine-shortfalls" />
                {(dispatched[0] || atTransnet[0] || atGrindrod[0]) && (
                    <WagonPipelineRibbon c={dispatched[0] || atTransnet[0] || atGrindrod[0]} />
                )}

                {/* Row 1 — three panels: Create · My Trains Overview · Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-3">
                    <Panel title="Create New Consignment" testid="mine-create-panel">
                        <a href="#creation-form" className="block w-full text-center py-4 rounded-lg text-white font-black uppercase tracking-wider"
                           style={{ backgroundColor: ACCENT }} data-testid="mine-create-cta">
                            + New Consignment
                        </a>
                    </Panel>
                    <Panel title="My Trains — Overview" testid="mine-overview">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <MiniTile icon={<IcnTrain size={22} color="#334155" />} label="Loading"    value={b.loading || 0}         sub="At mine" />
                            <MiniTile icon={<IcnTrain size={22} color="#166534" />} label="Dispatched" value={dispatched.length}       sub="In Transit" />
                            <MiniTile icon={<IcnYard  size={22} />}                  label="At Transnet" value={atTransnet.length}    sub="Staged" />
                            <MiniTile icon={<IcnCrane size={22} />}                  label="At Grindrod" value={atGrindrod.length}    sub="Tipping" />
                            <MiniTile icon={<IcnCheck size={22} color="#065F46" />}  label="Completed"  value={b.completed_today || 0} sub="Today" />
                        </div>
                    </Panel>
                    <Panel title="Performance (Today)" testid="mine-performance">
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                                <IcnTrain color={ACCENT} size={24} />
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Dispatched</div>
                                <div className="text-2xl font-black">{dispatchedToday}</div>
                            </div>
                            <div>
                                <div className="text-2xl">⚖</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Tonnes</div>
                                <div className="text-2xl font-black">{totalTonsToday.toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-2xl text-emerald-600">✓</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">On-Time</div>
                                <div className="text-2xl font-black">100%</div>
                            </div>
                        </div>
                    </Panel>
                </div>

                {/* Row 2 — My active trains + Train Progress */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <Panel title="My Active Trains" testid="mine-active-trains">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-slate-500 uppercase tracking-wider text-[10px]">
                                    <tr className="text-left border-b">
                                        <th className="py-1.5 pr-2">Consignment</th>
                                        <th className="py-1.5 pr-2">Train ID</th>
                                        <th className="py-1.5 pr-2 text-right">Wagons</th>
                                        <th className="py-1.5 pr-2 text-right">Tons</th>
                                        <th className="py-1.5 pr-2">Status</th>
                                        <th className="py-1.5 pr-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {active.slice(0, 8).map((c) => (
                                        <tr key={c.id} className={`border-b hover:bg-slate-50 cursor-pointer
                                                    ${highlight?.id === c.id ? "bg-amber-50" : ""}`}
                                            onClick={() => setSelected(c)}
                                            data-testid={`mine-row-${c.consignment_no}`}>
                                            <td className="py-1.5 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1.5 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1.5 pr-2 text-right">{c.wagon_count}</td>
                                            <td className="py-1.5 pr-2 text-right">{Number(c.tonnage || 0).toLocaleString()}</td>
                                            <td className="py-1.5 pr-2">{statusPill(c.status)}</td>
                                            <td className="py-1.5 pr-2">
                                                {c.status === "draft" && (
                                                    <button onClick={(e) => { e.stopPropagation(); startLoading(c); }}
                                                            data-testid={`mine-load-${c.consignment_no}`}
                                                            className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-black">LOAD</button>
                                                )}
                                                {(c.status === "loading" || c.status === "draft") && (
                                                    <button onClick={(e) => { e.stopPropagation(); dispatchTrain(c); }}
                                                            data-testid={`mine-dispatch-${c.consignment_no}`}
                                                            className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-black ml-1">DISPATCH</button>
                                                )}
                                                {["empty_wagons_returning", "empty_wagons_at_transnet", "empty_wagons_in_transit_to_mine"].includes(c.status) && (
                                                    <button onClick={(e) => { e.stopPropagation(); receiveEmpties(c); }}
                                                            data-testid={`mine-empties-${c.consignment_no}`}
                                                            className="px-2 py-0.5 rounded bg-amber-500 text-white text-[10px] font-black">EMPTIES IN</button>
                                                )}
                                                {!["draft", "loading",
                                                    "empty_wagons_returning", "empty_wagons_at_transnet",
                                                    "empty_wagons_in_transit_to_mine"].includes(c.status) && (
                                                    <span className="text-slate-400 text-[10px]">watching</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {active.length === 0 && (
                                        <tr><td colSpan={6} className="py-6 text-center text-slate-500">No active trains yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title={highlight ? `Train Progress — ${highlight.train_id || highlight.consignment_no}` : "Train Progress"}
                           testid="mine-train-progress">
                        {highlight ? <TrainFlow c={highlight} /> :
                            <div className="text-xs text-slate-500 py-6 text-center">Pick a train from the list.</div>}
                        {highlight && (
                            <div className="grid grid-cols-4 gap-2 mt-3">
                                <MiniStat label="Total Wagons" value={highlight.wagon_count || 0} />
                                <MiniStat label="Tipped"       value={`${highlight.wagons_tipped || 0} (${pct(highlight.wagons_tipped, highlight.wagon_count)}%)`} />
                                <MiniStat label="Remaining"    value={`${(highlight.wagon_count || 0) - (highlight.wagons_tipped || 0)}`} />
                                <MiniStat label="Est. Complete" value={fmtTime(highlight.eta_grindrod_arrival_at) || "—"} />
                            </div>
                        )}
                    </Panel>
                </div>

                {/* Row 3 — Creation · Dispatch · Events */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" id="creation-form">
                    <Panel title="Consignment Creation" testid="mine-form">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <Field label="Client">
                                <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                                       data-testid="mine-form-client" className="g-input" placeholder="Client Alpha Mining" />
                            </Field>
                            <Field label="Train ID">
                                <input value={form.train_id} onChange={(e) => setForm({ ...form, train_id: e.target.value })}
                                       data-testid="mine-form-train" className="g-input" placeholder="TRN006" />
                            </Field>
                            <Field label="Number of Wagons">
                                <input type="number" value={form.wagon_count}
                                       onChange={(e) => setForm({ ...form, wagon_count: e.target.value })}
                                       data-testid="mine-form-wagons" className="g-input" />
                            </Field>
                            <Field label="Coal Grade">
                                <select value={form.coal_grade}
                                        onChange={(e) => setForm({ ...form, coal_grade: e.target.value })}
                                        className="g-input">
                                    <option>RB1</option><option>RB2</option><option>RB3</option>
                                    <option>RB4</option><option>DUFF</option>
                                </select>
                            </Field>
                            <Field label="Planned Tons">
                                <input type="number" value={form.tonnage}
                                       onChange={(e) => setForm({ ...form, tonnage: e.target.value })}
                                       className="g-input" />
                            </Field>
                            <Field label="Planned Dispatch">
                                <input type="datetime-local" value={form.planned_dispatch_at}
                                       onChange={(e) => setForm({ ...form, planned_dispatch_at: e.target.value })}
                                       className="g-input" />
                            </Field>
                            <Field label="Comments (optional)" wide>
                                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                       className="g-input" placeholder="Enter comments…" />
                            </Field>
                        </div>
                        <button onClick={create} data-testid="mine-form-save"
                                className="mt-3 w-full py-2.5 rounded text-white font-black uppercase tracking-widest"
                                style={{ backgroundColor: ACCENT }}>
                            ✈ Create Consignment
                        </button>
                    </Panel>

                    <Panel title={dispatchable[0] ? `Dispatch Train — ${dispatchable[0].train_id || dispatchable[0].consignment_no}` : "Dispatch Train"}
                           testid="mine-dispatch-panel">
                        {dispatchable[0] ? (
                            <div className="text-xs">
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                    <Info label="Consignment No" value={dispatchable[0].consignment_no} />
                                    <Info label="Coal Grade"     value={dispatchable[0].coal_grade || dispatchable[0].product} />
                                    <Info label="Wagons"         value={dispatchable[0].wagon_count} />
                                    <Info label="Tons"           value={Number(dispatchable[0].tonnage || 0).toLocaleString()} />
                                    <Info label="Planned Dispatch" wide
                                          value={dispatchable[0].planned_dispatch_at
                                                  ? `${fmtDate(dispatchable[0].planned_dispatch_at)} ${fmtTime(dispatchable[0].planned_dispatch_at)}`
                                                  : "—"} />
                                </div>
                                <Field label="Comments (optional)">
                                    <input value={dispatchNotes} onChange={(e) => setDispatchNotes(e.target.value)}
                                           className="g-input" placeholder="Enter comments…" />
                                </Field>
                                <button onClick={() => dispatchTrain(dispatchable[0])}
                                        data-testid="mine-dispatch-btn"
                                        className="mt-3 w-full py-2.5 rounded text-white font-black uppercase tracking-widest bg-emerald-700 hover:bg-emerald-800">
                                    🚂 Dispatch Train
                                </button>
                            </div>
                        ) : (
                            <div className="text-xs text-slate-500 py-8 text-center">
                                No train ready to dispatch. Create a consignment first.
                            </div>
                        )}
                    </Panel>

                    <Panel title="Events — Today" testid="mine-events">
                        <div className="space-y-2 text-[11px]">
                            {notifs.items.slice(0, 6).map((n) => (
                                <div key={n.id} className="flex items-start gap-2 border-b last:border-0 pb-2 last:pb-0">
                                    <span className="text-slate-400 font-mono w-11 flex-shrink-0">{fmtTime(n.created_at)}</span>
                                    <div className="min-w-0">
                                        <div className="font-bold truncate">{n.message}</div>
                                        <div className="text-[9px] uppercase tracking-widest text-slate-400">{n.organisation}</div>
                                    </div>
                                </div>
                            ))}
                            {notifs.items.length === 0 && (
                                <div className="text-slate-500 py-6 text-center">No events yet today.</div>
                            )}
                        </div>
                    </Panel>
                </div>

                {/* Row 4 — Today's Summary + Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <Panel title="Today's Summary" testid="mine-summary">
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                            <MiniTile label="Trains Dispatched" value={dispatchedToday} icon={<IcnTrain size={20} />} />
                            <MiniTile label="En Route"          value={dispatched.length} icon={<IcnTrain color="#166534" size={20} />} />
                            <MiniTile label="At Transnet"       value={atTransnet.length} icon={<IcnYard size={20} />} />
                            <MiniTile label="At Grindrod"       value={atGrindrod.length} icon={<IcnCrane size={20} />} />
                            <MiniTile label="Wagons Dispatched" value={dispatched.reduce((s, c) => s + (c.wagon_count || 0), 0)} />
                            <MiniTile label="Tons Dispatched"   value={dispatched.reduce((s, c) => s + Number(c.tonnage || 0), 0).toLocaleString()} />
                        </div>
                    </Panel>
                    <Panel title="Quick Actions" testid="mine-quick">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-[10px] font-black uppercase tracking-widest">
                            <a href="#creation-form" className="p-3 rounded bg-emerald-50 text-emerald-800">➕ New Consignment</a>
                            <button className="p-3 rounded bg-blue-50 text-blue-800">🚆 View All Trains</button>
                            <button className="p-3 rounded bg-orange-50 text-orange-800">🕒 Dispatch History</button>
                            <button className="p-3 rounded bg-violet-50 text-violet-800">📊 Reports</button>
                        </div>
                    </Panel>
                </div>

                {/* Live Six-Sigma Analysis + production recommendations */}
                <RailSixSigmaPanel role="mine" />

                <div className="text-[10px] text-slate-400 pb-6">
                    All times are local (Africa/Johannesburg). Signed in as <b>{user?.name}</b>.
                </div>
            </main>
        </div>
    );
}

function pct(a, b) { const t = Number(b || 0); const n = Number(a || 0); return t ? Math.round(n * 100 / t) : 0; }
function MiniStat({ label, value }) {
    return (
        <div className="rounded bg-slate-50 border p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-lg font-black">{value}</div>
        </div>
    );
}
function MiniTile({ icon, label, value, sub }) {
    return (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
            {icon && <div className="grid place-items-center mb-0.5">{icon}</div>}
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className="text-xl font-black leading-tight">{value}</div>
            {sub && <div className="text-[9px] text-slate-500">{sub}</div>}
        </div>
    );
}
function Info({ label, value, wide }) {
    return (
        <div className={wide ? "col-span-2" : ""}>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="font-bold">{value}</div>
        </div>
    );
}

/* Horizontal 5-step process flow for a single train */
function TrainFlow({ c }) {
    const stages = [
        { key: "dispatched", label: "Dispatched · Mine",
          done: !!c.actual_dispatch_at, sub: c.actual_dispatch_at ? fmtTime(c.actual_dispatch_at) : "—",
          icon: <IcnMine size={22} color="#065F46" /> },
        { key: "transnet",   label: "At Transnet Yard",
          done: !!c.actual_transnet_arrival_at, sub: c.actual_transnet_arrival_at ? fmtTime(c.actual_transnet_arrival_at) : "—",
          icon: <IcnYard size={22} /> },
        { key: "released",   label: "Released to Grindrod",
          done: !!c.actual_transnet_release_at, sub: c.actual_transnet_release_at ? fmtTime(c.actual_transnet_release_at) : "—",
          icon: <IcnTrain size={22} color="#EA580C" /> },
        { key: "grindrod",   label: "At Grindrod (tipping)",
          done: !!c.actual_grindrod_arrival_at, sub: c.actual_grindrod_arrival_at ? "Since " + fmtTime(c.actual_grindrod_arrival_at) : "—",
          active: c.status === "tipping_in_progress",
          icon: <IcnCrane size={22} /> },
        { key: "completed",  label: "Completed",
          done: !!c.actual_completion_at, sub: c.actual_completion_at ? fmtTime(c.actual_completion_at) : "—",
          icon: <IcnCheck size={22} /> },
    ];
    return (
        <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
            {stages.map((s, i) => (
                <React.Fragment key={s.key}>
                    <FlowStep label={s.label} sub={s.sub} done={s.done} active={s.active} icon={s.icon} />
                    {i < stages.length - 1 && <FlowArrow />}
                </React.Fragment>
            ))}
        </div>
    );
}
